import { withNetworkUtm } from "@/lib/network";

type NetworkLink = {
  label: string;
  href: string;
  internal?: boolean;
  disabled?: boolean;
  current?: boolean;
  /** UTM campaign slug when this link goes to a Shrink Network property. */
  campaign?: string;
};

type NetworkColumn = {
  heading: string;
  links: NetworkLink[];
};

const COLUMNS: NetworkColumn[] = [
  {
    heading: "Understand",
    links: [
      { label: "Shrinkopedia", href: "https://shrinkopedia.com/" },
      { label: "Shrinktionary", href: "https://shrinktionary.com/" },
      { label: "ShrinkDaily", href: "https://shrinkdaily.com/" },
      { label: "AnxietyResource", href: "https://anxietyresource.org/" },
      { label: "DepressionResource", href: "https://depressionresource.org/" },
      { label: "AnxietyResearch", href: "https://anxietyresearch.org/" },
      { label: "PsychiatryRx", href: "https://psychiatryrx.org/" },
    ],
  },
  {
    heading: "Apply",
    links: [
      { label: "shrinQ", href: "https://shrinq.com/" },
      { label: "Unstuck", href: "https://beunstuck.app/" },
      { label: "QuitScrolling", href: "https://quitscrolling.app/" },
    ],
  },
  {
    heading: "Care",
    links: [{ label: "shrinkMD", href: "https://shrinkmd.com/" }],
  },
  {
    heading: "Profession",
    links: [
      { label: "shrinkiatry", href: "https://shrinkiatry.com/" },
      { label: "Shariq Refai", href: "/", internal: true, current: true },
    ],
  },
];

const BOTTOM_LINKS: NetworkLink[] = [
  {
    label: "Network editorial standards",
    href: "https://shrinknetwork.com/editorial-standards/",
  },
  { label: "Crisis and safety", href: "https://shrinknetwork.com/crisis/" },
  { label: "About the network", href: "https://shrinknetwork.com/" },
  { label: "Contact", href: "https://shrinknetwork.com/contact/" },
];

const CREAM = "#F7F3ED";

function NetworkAnchor({
  link,
  className,
}: {
  link: NetworkLink;
  className: string;
}) {
  if (link.disabled) {
    return (
      <span className={`${className} opacity-50 cursor-default`}>
        {link.label}
      </span>
    );
  }
  if (link.internal) {
    return (
      <a href={link.href} className={className}>
        {link.label}
        {link.current ? (
          <span className="ml-2 text-[0.65rem] font-bold uppercase tracking-[0.15em] text-[#58B8B1]">
            <span className="sr-only">This site: </span>You are here
          </span>
        ) : null}
      </a>
    );
  }
  return (
    <a
      href={withNetworkUtm(link.href, link.campaign ?? "network_footer")}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      {link.label}
    </a>
  );
}

export function NetworkFooter() {
  return (
    <section
      aria-label="The Shrink Network"
      className="bg-[#171717] border-t border-white/10"
    >
      <div className="container mx-auto px-4 md:px-8 py-16">
        {/* Top row */}
        <div className="flex items-start gap-4">
          <span
            aria-hidden="true"
            className="font-serif italic text-3xl leading-none text-[#58B8B1] mt-0.5"
          >
            n
          </span>
          <div>
            <span className="block text-xs font-bold uppercase tracking-[0.2em] text-[#58B8B1]">
              Part of{" "}
              <a
                href={withNetworkUtm("https://shrinknetwork.com/", "network_footer_headline")}
                target="_blank"
                rel="noopener noreferrer"
                className="underline decoration-[#58B8B1]/40 underline-offset-4 transition-colors hover:decoration-[#58B8B1] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#58B8B1] rounded-sm"
              >
                The Shrink Network
              </a>
            </span>
            <p
              className="mt-4 max-w-2xl text-sm font-light leading-relaxed"
              style={{ color: CREAM }}
            >
              The Shrink Network is an independent mental health knowledge
              ecosystem built around one idea: mental health is too important to
              squeeze into one website. Each site has one job. Together, they
              help people understand, apply, and access care. No matter where you
              enter the network, we help you find the next step that makes sense.
            </p>
            <span
              className="mt-4 block font-serif italic text-lg"
              style={{ color: CREAM }}
            >
              Your mind. Understood.
            </span>
          </div>
        </div>

        {/* Columns */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {COLUMNS.map((col) => (
            <nav
              key={col.heading}
              aria-label={`${col.heading} (The Shrink Network)`}
              className="flex flex-col gap-4"
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#58B8B1]">
                {col.heading}
              </span>
              <ul className="flex flex-col gap-3 list-none p-0">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <NetworkAnchor
                      link={link}
                      className="text-sm text-[#F7F3ED]/85 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#58B8B1] rounded-sm"
                    />
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        {/* Bottom row */}
        <div className="mt-12 pt-8 border-t border-white/10 space-y-3">
          <p className="text-xs leading-relaxed" style={{ color: CREAM }}>
            The Shrink Network is independent. Each site is its own resource with
            its own purpose.
          </p>
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            {BOTTOM_LINKS.map((link, i) => (
              <span key={link.label} className="flex items-center gap-x-2">
                <NetworkAnchor
                  link={link}
                  className="text-[#F7F3ED]/85 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#58B8B1] rounded-sm"
                />
                {i < BOTTOM_LINKS.length - 1 ? (
                  <span aria-hidden="true" className="opacity-40">
                    &middot;
                  </span>
                ) : null}
              </span>
            ))}
          </p>
          <p className="text-xs" style={{ color: CREAM }}>
            &copy; 2026 The Shrink Network.
          </p>
          <p className="text-xs leading-relaxed" style={{ color: CREAM }}>
            shrinkMD<sup>&reg;</sup> (U.S. Reg. No. 7,403,744) and
            shrinQ<sup>&reg;</sup> (U.S. Reg. No. 8,189,949) are federally
            registered service marks of Shariq Refai, MD, MBA, used with
            permission. shariqrefai.com is published by shrinkMD Publishing, LLC.
          </p>
        </div>
      </div>
    </section>
  );
}
